import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack, LocalOffer, NotificationsActive } from '@mui/icons-material';
import { expertPaymentService } from '../../services/expert-payment.service';
import { SubscriptionPlan } from '../../types/payment';
import { toastService } from '../../services/toast.service';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/auth/authSlice';
import { PAYMENT_CONFIG } from '../../config';
import { loadWayForPayWidget } from '../../utils/wayforpay-loader';
import { addDays, format } from 'date-fns';
import { uk } from 'date-fns/locale';
import api from '../../services/api';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

// Independent from PaymentModal.tsx on purpose: client and expert checkout flows
// are expected to diverge, so they are not sharing a single parametrized component.

interface ExpertPaymentModalProps {
  open: boolean;
  onClose: () => void;
  canClose?: boolean;
  onSuccess?: () => void;
  initialCanUseTrial?: boolean;
}

type BillingCycle = 'month' | 'year';

export const ExpertPaymentModal: React.FC<ExpertPaymentModalProps> = ({ open, onClose, canClose = false, onSuccess, initialCanUseTrial }) => {
  const user = useAppSelector(selectUser);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('month');
  const [canUseTrial, setCanUseTrial] = useState<boolean>(initialCanUseTrial ?? false);

  // Wizard State
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(true);

  // Offer Modal State
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [offerContent, setOfferContent] = useState<string>('');
  const [loadingOffer, setLoadingOffer] = useState(false);
  const [publicOfferLink, setPublicOfferLink] = useState<string | null>(null);

  const wayforpayRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      loadData();
      setStep('select');
      setSelectedPlan(null);
      setAgreedToTerms(true);
    }
  }, [open]);

  useEffect(() => {
    if (initialCanUseTrial !== undefined) {
      setCanUseTrial(initialCanUseTrial);
    }
  }, [initialCanUseTrial]);

  // WayForPay widget event listener. No realtime WebSocket fallback here (MVP for
  // the expert cabinet relies on the widget's own callback + this postMessage event;
  // the settings tab re-fetches the subscription status after the modal closes).
  useEffect(() => {
    const handleWfpMessage = (event: MessageEvent) => {
      if (event.data === 'WfpWidgetEventApproved') {
        handleSuccess();
      }
      if (event.data === 'WfpWidgetEventClose') {
        setProcessing(false);
      }
    };

    window.addEventListener('message', handleWfpMessage);

    return () => {
      window.removeEventListener('message', handleWfpMessage);
    };
  }, [onClose, onSuccess]);

  const handleSuccess = () => {
    if (wayforpayRef.current && typeof wayforpayRef.current.closeit === 'function') {
      wayforpayRef.current.closeit();
    }
    toastService.success('Оплата успішна! Підписку активовано.');
    onClose();
    if (onSuccess) onSuccess();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansResponse, statusData] = await Promise.all([
        expertPaymentService.getPlans(),
        initialCanUseTrial === undefined ? expertPaymentService.getMySubscription() : Promise.resolve(null),
      ]);

      if (plansResponse && Array.isArray(plansResponse.plans)) {
        setPlans(plansResponse.plans);
        if (plansResponse.links?.publicOffer) {
          setPublicOfferLink(plansResponse.links.publicOffer);
        }
      } else {
        setPlans([]);
        toastService.error('Некоректний формат даних тарифів');
      }

      if (statusData) {
        setCanUseTrial(!!statusData.canUseTrial);
      }
    } catch (error) {
      console.error('Failed to load expert payment data', error);
      toastService.error('Не вдалося завантажити дані');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOffer = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!publicOfferLink) {
      toastService.error('Посилання на оферту не знайдено');
      return;
    }

    setIsOfferModalOpen(true);
    setLoadingOffer(true);

    try {
      const response = await api.get(publicOfferLink);
      setOfferContent(response.data);
    } catch (error) {
      console.error('Failed to load public offer', error);
      toastService.error('Не вдалося завантажити текст оферти');
      setOfferContent('<p>Не вдалося завантажити текст оферти. Будь ласка, спробуйте пізніше.</p>');
    } finally {
      setLoadingOffer(false);
    }
  };

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setStep('confirm');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedPlan(null);
  };

  const handleConfirmPayment = async () => {
    if (!user || !selectedPlan || !agreedToTerms) return;
    setProcessing(true);
    try {
      const Wayforpay = await loadWayForPayWidget();
      const wayforpay = new Wayforpay();
      wayforpayRef.current = wayforpay;

      const params = await expertPaymentService.initPayment(selectedPlan.id, billingCycle);

      wayforpay.run(
        params,
        async function (response: any) {
          try {
            await expertPaymentService.sendPaymentCallback(response);
          } catch (err) {
            console.warn('Failed to send expert payment callback (might be handled by webhook)', err);
          }

          handleSuccess();
        },
        function () {
          toastService.error('Оплату відхилено. Спробуйте іншу картку.');
          setProcessing(false);
        },
        function () {
          toastService.info('Оплата в обробці...');
        },
      );
    } catch (error: any) {
      console.error('Expert payment init error:', error);
      setProcessing(false);
      toastService.error(error.message || 'Помилка ініціалізації оплати');
    }
  };

  const handleCycleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCycle: BillingCycle | null,
  ) => {
    if (newCycle !== null) {
      setBillingCycle(newCycle);
    }
  };

  const getPlanDetails = (plan: SubscriptionPlan) => {
    const priceMonth = plan.prices?.month || plan.priceMonth;
    const priceYear = plan.prices?.year || plan.priceYear;
    const trialPrice = plan.trial?.price || plan.trialPrice;
    const trialDays = plan.trial?.days || plan.trialDays;

    return { priceMonth, priceYear, trialPrice, trialDays };
  };

  const getPriceDisplay = (plan: SubscriptionPlan) => {
    const { priceMonth, priceYear, trialPrice, trialDays } = getPlanDetails(plan);

    if (priceMonth && priceYear) {
      const price = billingCycle === 'month' ? priceMonth : priceYear;

      const hasTrialConfig = !!(trialPrice && trialDays);
      const showTrial = hasTrialConfig && canUseTrial;

      if (showTrial) {
        return (
          <Box>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              {trialPrice} {plan.currency}
              <Chip label="TRIAL" color="success" size="small" icon={<LocalOffer />} />
            </Typography>
            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
              {price} {plan.currency}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              перші {trialDays} днів
            </Typography>
          </Box>
        );
      }

      return (
        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
          {price} {plan.currency}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            / {billingCycle === 'month' ? 'міс' : 'рік'}
          </Typography>
        </Typography>
      );
    }

    return (
      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
        {plan.price} {plan.currency}
      </Typography>
    );
  };

  const renderSelectionStep = () => {
    const hasAnyTrialConfig = plans.some((p) => {
      const details = getPlanDetails(p);
      return !!(details.trialPrice && details.trialDays);
    });
    const showPromoMessage = hasAnyTrialConfig && canUseTrial;

    return (
      <>
        {showPromoMessage ? (
          <Typography variant="body1" gutterBottom sx={{ mb: 3, fontWeight: 500, color: 'primary.main' }}>
            Спробуйте повний функціонал за спеціальною ціною для нових експертів.
          </Typography>
        ) : (
          <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
            Отримайте повний доступ до всіх функцій платформи.
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <ToggleButtonGroup value={billingCycle} exclusive onChange={handleCycleChange} color="primary">
            <ToggleButton value="month">Щомісяця</ToggleButton>
            <ToggleButton value="year">Щорічно (-20%)</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={plans.length === 1 ? 12 : 6} key={plan.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e0e0e0',
                transition: '0.3s',
                '&:hover': { boxShadow: 6, borderColor: 'primary.main' },
              }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Typography variant="h5" component="div" gutterBottom>
                    {plan.name}
                  </Typography>

                  <Box sx={{ my: 2 }}>{getPriceDisplay(plan)}</Box>

                  <Typography variant="body2" color="text.secondary">
                    {plan.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button variant="contained" size="large" onClick={() => handlePlanSelect(plan)}>
                    Обрати
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
          {plans.length === 0 && (
            <Grid item xs={12}>
              <Typography align="center" color="text.secondary">
                Немає доступних тарифів.
              </Typography>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  const renderConfirmationStep = () => {
    if (!selectedPlan) return null;

    const { priceMonth, priceYear, trialPrice, trialDays } = getPlanDetails(selectedPlan);

    const hasTrialConfig = !!(trialPrice && trialDays);
    const showTrial = hasTrialConfig && canUseTrial;

    const regularPrice = billingCycle === 'month' ? priceMonth : priceYear;
    const payNowPrice = showTrial ? trialPrice : regularPrice;

    let monthlyEquivalent = '';
    if (billingCycle === 'year' && regularPrice) {
      const priceNum = parseFloat(regularPrice);
      if (!isNaN(priceNum)) {
        monthlyEquivalent = (priceNum / 12).toFixed(2);
      }
    }

    const nextPaymentDate = showTrial && trialDays ? addDays(new Date(), trialDays) : null;

    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Назад до тарифів
        </Button>

        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Підсумок замовлення
            </Typography>
            <List disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="Тарифний план" />
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedPlan.name} ({billingCycle === 'month' ? 'Місячний' : 'Річний'})
                </Typography>
              </ListItem>
              <Divider />

              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="До сплати зараз" secondary={showTrial ? 'Акційна пропозиція' : undefined} />
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  {payNowPrice} {selectedPlan.currency}
                </Typography>
              </ListItem>

              {showTrial && nextPaymentDate && (
                <>
                  <ListItem sx={{ px: 0, bgcolor: 'rgba(76, 175, 80, 0.08)', borderRadius: 1, p: 1, mt: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LocalOffer color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" color="text.primary" fontWeight="bold">
                          Наступне списання: {format(nextPaymentDate, 'd MMMM yyyy', { locale: uk })}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography variant="h6" color="text.primary" fontWeight="bold" component="span">
                            {regularPrice} {selectedPlan.currency} / {billingCycle === 'month' ? 'міс' : 'рік'}
                          </Typography>
                          {billingCycle === 'year' && monthlyEquivalent && (
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                              ({monthlyEquivalent} {selectedPlan.currency} / міс)
                            </Typography>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>

                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <NotificationsActive color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Ми надішлемо нагадування за 3 дні до списання"
                      primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </CardContent>
        </Card>

        <Box sx={{ bgcolor: 'info.lighter', p: 2, borderRadius: 1, mb: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} color="primary" />}
            label={
              <Typography variant="caption" color="text.secondary">
                Я погоджуюсь з умовами підписки та автоматичним списанням коштів
                {billingCycle === 'month' ? ' щомісяця' : ' щорічно'}.
                <br />
                <Link href="#" onClick={handleOpenOffer} sx={{ mt: 1, display: 'inline-block', cursor: 'pointer' }}>
                  Публічна оферта
                </Link>
              </Typography>
            }
          />
        </Box>

        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleConfirmPayment}
            disabled={processing || !agreedToTerms}
            sx={{ minWidth: 200 }}
          >
            {processing ? 'Обробка...' : `Оплатити ${payNowPrice} ${selectedPlan.currency}`}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={canClose ? onClose : undefined}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={!canClose}
        data-i-doc={ModalIndices.EXPERT_PAYMENT}
      >
        <DialogTitle>
          {step === 'select' ? 'Оберіть тарифний план' : 'Підтвердження підписки'}
          {PAYMENT_CONFIG.enableSandbox && (
            <Typography variant="caption" sx={{ float: 'right', color: 'warning.main', mt: 1 }}>
              Sandbox Mode
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          {step === 'select' && renderSelectionStep()}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            step === 'confirm' ? renderConfirmationStep() : null
          )}
        </DialogContent>

        {canClose && step === 'select' && (
          <DialogActions>
            <Button onClick={onClose}>Закрити</Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog open={isOfferModalOpen} onClose={() => setIsOfferModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Публічна оферта</DialogTitle>
        <DialogContent dividers>
          {loadingOffer ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: offerContent }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOfferModalOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
